import React, { useState, useEffect } from 'react';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  TrendingUp,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  UserCheck,
  Activity,
  ClipboardList,
  Settings,
  Headphones,
  User,
} from 'lucide-react';
import { adminAPI, supportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatPlanName } from '../../utils/planNameHelper';
import CreateOrganizationModal from '../admin/CreateOrganizationModal';
import EditOrganizationModal from '../admin/EditOrganizationModal';
import OrganizationMetricsModal from '../admin/OrganizationMetricsModal';
import QuestionnaireList from '../questionnaires/QuestionnaireList';
import QuestionnaireBuilder from '../questionnaires/QuestionnaireBuilder';
import ShareQuestionnaireModal from '../questionnaires/ShareQuestionnaireModal';
import AdminSettings from '../admin/AdminSettings';
import SupportDashboard from '../support/SupportDashboard';
import DarkModeToggle from '../common/DarkModeToggle';
import EditPartnerModal from '../admin/EditPartnerModal';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [activeTab, setActiveTab] = useState('organizations'); // 'organizations', 'questionnaires', 'partners'

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditPartnerModal, setShowEditPartnerModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [partnerModalLoading, setPartnerModalLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Questionnaire states
  const [questionnaireView, setQuestionnaireView] = useState('list'); // 'list', 'create', 'edit'
  const [editingQuestionnaireId, setEditingQuestionnaireId] = useState(null);
  const [sharingQuestionnaire, setSharingQuestionnaire] = useState(null);

  // Support unread count state
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  // Helper function to format plan display names
  const getPlanDisplayName = (plan) => {
    const planMap = {
      'basic': 'Plan Basic',
      'basic_silver': 'Plan Basic - Silver',
      'basic_gold': 'Plan Basic - Gold',
      'pro_silver': 'Plan Pro - Silver',
      'pro_gold': 'Plan Pro - Gold',
      'pro_platinum': 'Plan Pro - Platinum'
    };
    return planMap[plan] || plan;
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'partners') {
      loadPartners();
    }
  }, [activeTab]);

  // Load support unread count for admins (all admins have query_resolver access)
  useEffect(() => {
    loadSupportUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(loadSupportUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSupportUnreadCount = async () => {
    try {
      const response = await supportAPI.getConversations();
      const conversations = response.data.conversations || [];
      const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setSupportUnreadCount(totalUnread);
    } catch (err) {
      console.error('Failed to load support unread count:', err);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [orgsResponse, statsResponse] = await Promise.all([
        adminAPI.getAllOrganizations(),
        adminAPI.getDashboardStats(),
      ]);
      setOrganizations(orgsResponse.data.organizations);
      setStats(statsResponse.data.stats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      alert('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      setPartnersLoading(true);
      const response = await adminAPI.getAllPartners();
      setPartners(response.data.partners || []);
    } catch (error) {
      console.error('Error loading partners:', error);
      alert('Failed to load partners');
    } finally {
      setPartnersLoading(false);
    }
  };

  const handleCreateOrganization = async (formData) => {
    try {
      setModalLoading(true);
      await adminAPI.createOrganization(formData);
      alert('Organization created successfully!');
      setShowCreateModal(false);
      loadDashboardData();
    } catch (error) {
      console.error('Error creating organization:', error);
      alert(error.response?.data?.error || 'Failed to create organization');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateOrganization = async (formData) => {
    try {
      setModalLoading(true);
      await adminAPI.updateOrganization(selectedOrg.id, formData);
      alert('Organization updated successfully!');
      setShowEditModal(false);
      setSelectedOrg(null);
      loadDashboardData();
    } catch (error) {
      console.error('Error updating organization:', error);
      alert(error.response?.data?.error || 'Failed to update organization');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeactivateOrganization = async (org) => {
    if (!window.confirm(`Are you sure you want to deactivate "${org.name}"? They will not be able to login.`)) {
      return;
    }

    try {
      await adminAPI.deactivateOrganization(org.id);
      alert('Organization deactivated successfully!');
      loadDashboardData();
    } catch (error) {
      console.error('Error deactivating organization:', error);
      alert(error.response?.data?.error || 'Failed to deactivate organization');
    }
  };

  const handleActivateOrganization = async (org) => {
    try {
      await adminAPI.activateOrganization(org.id);
      alert('Organization activated successfully!');
      loadDashboardData();
    } catch (error) {
      console.error('Error activating organization:', error);
      alert(error.response?.data?.error || 'Failed to activate organization');
    }
  };

  const handleDeleteOrganization = async (org) => {
    if (!window.confirm(
      `⚠️ WARNING: Are you sure you want to PERMANENTLY DELETE "${org.name}"?\n\n` +
      `This will delete:\n` +
      `- All partners (${org.total_partners || 0})\n` +
      `- All clients (${org.total_clients || 0})\n` +
      `- All sessions (${org.total_sessions || 0})\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type the organization name to confirm.`
    )) {
      return;
    }

    const confirmation = window.prompt(`Please type "${org.name}" to confirm deletion:`);
    if (confirmation !== org.name) {
      alert('Organization name does not match. Deletion cancelled.');
      return;
    }

    try {
      await adminAPI.deleteOrganization(org.id);
      alert('Organization deleted permanently!');
      loadDashboardData();
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  const handleViewMetrics = async (org) => {
    try {
      setSelectedOrg(org);
      setShowMetricsModal(true);
      setMetricsLoading(true);
      const response = await adminAPI.getOrganizationMetrics(org.id);
      setMetricsData(response.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
      alert('Failed to load organization metrics');
      setShowMetricsModal(false);
    } finally {
      setMetricsLoading(false);
    }
  };

  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' ||
                         (filterStatus === 'active' && org.is_active) ||
                         (filterStatus === 'inactive' && !org.is_active);
    
    return matchesSearch && matchesFilter;
  });

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'primary' }) => {
    // Icon color classes for dynamic colors
    const iconBgClass = {
      'primary': 'bg-primary-100',
      'blue': 'bg-primary-100',
      'green': 'bg-green-100',
      'purple': 'bg-primary-200'
    }[color] || 'bg-primary-100';

    const iconColorClass = {
      'primary': 'text-primary-700',
      'blue': 'text-primary-700',
      'green': 'text-green-600',
      'purple': 'text-primary-600'
    }[color] || 'text-primary-700';

    const borderClass = {
      'primary': 'border-primary-600',
      'blue': 'border-primary-600',
      'green': 'border-green-600',
      'purple': 'border-primary-500'
    }[color] || 'border-primary-600';

    return (
      <div className={`bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md p-6 border-l-4 ${borderClass} flex-shrink-0 w-full lg:w-auto`}>
        <div className="flex items-center justify-between h-full min-h-[120px]">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 dark:text-dark-text-secondary">{label}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mt-2">{value}</p>
            <div className="h-6">
              {subValue && <p className="text-sm text-gray-500 dark:text-dark-text-tertiary mt-1">{subValue}</p>}
            </div>
          </div>
          <div className={`${iconBgClass} p-4 rounded-full`}>
            <Icon className={`h-8 w-8 ${iconColorClass}`} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const handleQuestionnaireEdit = (questionnaireId) => {
    setEditingQuestionnaireId(questionnaireId);
    setQuestionnaireView('edit');
  };

  const handleQuestionnaireShare = (questionnaire) => {
    setSharingQuestionnaire(questionnaire);
  };

  const handleQuestionnaireCopy = () => {
    // Reload is handled by QuestionnaireList
  };

  const handleQuestionnaireSave = () => {
    setQuestionnaireView('list');
    setEditingQuestionnaireId(null);
  };

  const handleQuestionnaireCancel = () => {
    setQuestionnaireView('list');
    setEditingQuestionnaireId(null);
  };

  const handleUpdatePartner = async (formData) => {
    try {
      setPartnerModalLoading(true);
      await adminAPI.updatePartner(selectedPartner.id, formData);
      alert('Partner updated successfully!');
      setShowEditPartnerModal(false);
      setSelectedPartner(null);
      loadPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      alert(error.response?.data?.error || 'Failed to update partner');
    } finally {
      setPartnerModalLoading(false);
    }
  };

  const filteredPartners = partners.filter((partner) => {
    const matchesSearch = partner.name?.toLowerCase().includes(partnerSearchTerm.toLowerCase()) ||
                         partner.email?.toLowerCase().includes(partnerSearchTerm.toLowerCase()) ||
                         partner.partner_id?.toLowerCase().includes(partnerSearchTerm.toLowerCase()) ||
                         partner.organization_name?.toLowerCase().includes(partnerSearchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Admin Dashboard</h1>
          <p className="text-gray-600 dark:text-dark-text-secondary mt-1">Manage organizations and view system statistics</p>
        </div>
        {activeTab === 'organizations' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary-700 text-white px-6 py-3 rounded-lg hover:bg-primary-800 transition-colors flex items-center space-x-2 font-medium shadow-md"
          >
            <Plus className="h-5 w-5" />
            <span>Create Organization</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md dark:shadow-lg dark:shadow-black/20">
        {/* Mobile Dark Mode Toggle */}
        <div className="lg:hidden border-b border-gray-200 dark:border-dark-border py-3 px-4">
          <DarkModeToggle variant="button" showLabel />
        </div>

        <div className="border-b border-gray-200 dark:border-dark-border">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveTab('organizations')}
              className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'organizations'
                  ? 'border-primary-600 text-primary-700 dark:border-dark-primary-500 dark:text-dark-primary-500'
                  : 'border-transparent text-umbra-500 hover:text-umbra-700 hover:border-umbra-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary dark:hover:border-dark-border'
              }`}
            >
              <Building2 className="h-5 w-5" />
              Organizations
            </button>
            <button
              onClick={() => setActiveTab('questionnaires')}
              className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'questionnaires'
                  ? 'border-primary-600 text-primary-700 dark:border-dark-primary-500 dark:text-dark-primary-500'
                  : 'border-transparent text-umbra-500 hover:text-umbra-700 hover:border-umbra-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary dark:hover:border-dark-border'
              }`}
            >
              <ClipboardList className="h-5 w-5" />
              Questionnaires
            </button>
            <button
              onClick={() => setActiveTab('partners')}
              className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'partners'
                  ? 'border-primary-600 text-primary-700 dark:border-dark-primary-500 dark:text-dark-primary-500'
                  : 'border-transparent text-umbra-500 hover:text-umbra-700 hover:border-umbra-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary dark:hover:border-dark-border'
              }`}
            >
              <User className="h-5 w-5" />
              Partners
            </button>
            <button
            onClick={() => setActiveTab('support')}
            className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 relative ${
              activeTab === 'support'
                ? 'border-primary-600 text-primary-700 dark:border-dark-primary-500 dark:text-dark-primary-500'
                : 'border-transparent text-umbra-500 hover:text-umbra-700 hover:border-umbra-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary dark:hover:border-dark-border'
            }`}
          >
            <div className="relative">
              <Headphones className="h-5 w-5" />
              {supportUnreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
                </span>
              )}
            </div>
            Support
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'settings'
                ? 'border-primary-600 text-primary-700 dark:border-dark-primary-500 dark:text-dark-primary-500'
                : 'border-transparent text-umbra-500 hover:text-umbra-700 hover:border-umbra-300 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary dark:hover:border-dark-border'
            }`}
          >
            <Settings className="h-5 w-5" />
            Settings
          </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'organizations' && (
        <>

      {/* Stats Overview */}
      {stats && (
        <>
          {/* Mobile: Horizontal Scrollable Cards */}
          <div className="lg:hidden overflow-x-auto -mx-4 px-4 scrollbar-thin scroll-smooth">
            <div className="flex gap-4 pb-2">
              <div className="w-[70%] flex-shrink-0">
                <StatCard
                  icon={Building2}
                  label="Total Organizations"
                  value={stats.total_organizations || 0}
                  subValue={`${stats.active_organizations || 0} active`}
                  color="primary"
                />
              </div>
              <div className="w-[70%] flex-shrink-0">
                <StatCard
                  icon={UserCheck}
                  label="Total Partners"
                  value={stats.total_partners || 0}
                  color="blue"
                />
              </div>
              <div className="w-[70%] flex-shrink-0">
                <StatCard
                  icon={Users}
                  label="Total Clients"
                  value={stats.total_users || 0}
                  color="green"
                />
              </div>
              <div className="w-[70%] flex-shrink-0">
                <StatCard
                  icon={Activity}
                  label="Total Sessions"
                  value={stats.total_sessions || 0}
                  subValue={`${stats.sessions_this_month || 0} this month`}
                  color="primary"
                />
              </div>
            </div>
          </div>

          {/* Desktop: Grid Layout */}
          <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Building2}
              label="Total Organizations"
              value={stats.total_organizations || 0}
              subValue={`${stats.active_organizations || 0} active`}
              color="indigo"
            />
            <StatCard
              icon={UserCheck}
              label="Total Partners"
              value={stats.total_partners || 0}
              color="blue"
            />
            <StatCard
              icon={Users}
              label="Total Clients"
              value={stats.total_users || 0}
              color="green"
            />
            <StatCard
              icon={Activity}
              label="Total Sessions"
              value={stats.total_sessions || 0}
              subValue={`${stats.sessions_this_month || 0} this month`}
              color="purple"
            />
          </div>
        </>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-dark-text-tertiary" />
            <input
              type="text"
              placeholder="Search organizations by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-umbra-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-primary-700 text-white'
                  : 'bg-umbra-100 text-umbra-700 hover:bg-umbra-200'
              }`}
            >
              All ({organizations.length})
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active ({organizations.filter(o => o.is_active).length})
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'inactive'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Inactive ({organizations.filter(o => !o.is_active).length})
            </button>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Partners
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Clients
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  TheraPTrack Controlled
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  For New Therapists
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-tertiary divide-y divide-gray-200 dark:divide-dark-border">
              {filteredOrganizations.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <Building2 className="h-12 w-12 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-dark-text-secondary">
                      {searchTerm ? 'No organizations found matching your search' : 'No organizations yet'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-gray-400 dark:text-dark-text-tertiary mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{org.name}</div>
                          <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{org.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text-primary">{org.contact}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {org.subscription_plan ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {getPlanDisplayName(org.subscription_plan)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-dark-text-tertiary">No plan</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{org.total_partners || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{org.total_clients || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{org.total_sessions || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {org.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {org.theraptrack_controlled ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {org.theraptrack_controlled ? (
                        <div className="flex items-center justify-center">
                          <label className="relative inline-flex items-center cursor-not-allowed opacity-75" title="Edit organization to change this setting">
                            <input
                              type="checkbox"
                              checked={org.for_new_therapists || false}
                              disabled
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                            {org.for_new_therapists && (
                              <span className="ml-2 text-xs font-medium text-primary-600 dark:text-primary-400">Active</span>
                            )}
                          </label>
                          <span className="ml-2 text-xs text-gray-500 dark:text-dark-text-tertiary" title="Edit organization to change this setting">
                            (Edit to change)
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-dark-text-tertiary">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewMetrics(org)}
                          className="text-primary-700 dark:text-dark-primary-400 hover:text-primary-900 dark:hover:text-dark-primary-300 p-1 rounded hover:bg-primary-50 dark:hover:bg-dark-bg-secondary"
                          title="View Metrics"
                        >
                          <TrendingUp className="h-5 w-5" />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              // Fetch full organization details to ensure all fields are present
                              const response = await adminAPI.getOrganizationById(org.id);
                              setSelectedOrg(response.data.organization);
                              setShowEditModal(true);
                            } catch (error) {
                              console.error('Error fetching organization:', error);
                              // Fallback to org from list if fetch fails
                              setSelectedOrg(org);
                              setShowEditModal(true);
                            }
                          }}
                          className="text-primary-700 dark:text-dark-primary-400 hover:text-primary-900 dark:hover:text-dark-primary-300 p-1 rounded hover:bg-primary-50 dark:hover:bg-dark-bg-secondary"
                          title="Edit"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        {org.is_active ? (
                          <button
                            onClick={() => handleDeactivateOrganization(org)}
                            className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                            title="Deactivate"
                          >
                            <AlertTriangle className="h-5 w-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateOrganization(org)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 p-1 rounded hover:bg-green-50 dark:hover:bg-dark-bg-secondary"
                            title="Activate"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteOrganization(org)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Delete Permanently"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {activeTab === 'questionnaires' && (
        <div>
          {questionnaireView === 'list' && (
            <QuestionnaireList
              ownerType="admin"
              ownerId={user.id}
              onEdit={handleQuestionnaireEdit}
              onShare={handleQuestionnaireShare}
              onCreateNew={() => setQuestionnaireView('create')}
            />
          )}
          {questionnaireView === 'create' && (
            <QuestionnaireBuilder
              onSave={handleQuestionnaireSave}
              onCancel={handleQuestionnaireCancel}
            />
          )}
          {questionnaireView === 'edit' && editingQuestionnaireId && (
            <QuestionnaireBuilder
              questionnaireId={editingQuestionnaireId}
              onSave={handleQuestionnaireSave}
              onCancel={handleQuestionnaireCancel}
            />
          )}
        </div>
      )}

      {activeTab === 'partners' && (
        <>
          {/* Search */}
          <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-dark-text-tertiary" />
              <input
                type="text"
                placeholder="Search partners by name, email, partner ID, or organization..."
                value={partnerSearchTerm}
                onChange={(e) => setPartnerSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-umbra-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
              />
            </div>
          </div>

          {/* Partners Table */}
          <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Partner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Partner ID
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Clients
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Subscription Plan
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Query Resolver
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-bg-tertiary divide-y divide-gray-200 dark:divide-dark-border">
                  {partnersLoading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <User className="h-12 w-12 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-dark-text-secondary">
                          {partnerSearchTerm ? 'No partners found matching your search' : 'No partners yet'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((partner) => (
                      <tr key={partner.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <User className="h-5 w-5 text-gray-400 dark:text-dark-text-tertiary mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{partner.name}</div>
                              {partner.email && (
                                <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{partner.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-dark-text-primary">{partner.organization_name || 'N/A'}</div>
                          {partner.theraptrack_controlled && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 mt-1">
                              TheraPTrack Controlled
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{partner.partner_id || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">{partner.total_clients || 0}</span>
                        </td>
                        <td className="px-6 py-4">
                          {partner.subscription_plan_name ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                                {formatPlanName(partner.subscription_plan_name)}
                              </div>
                              {partner.subscription_plan_duration_days && partner.subscription_plan_duration_days > 0 && (
                                <div className="text-xs text-gray-500 dark:text-dark-text-secondary mt-1">
                                  {partner.subscription_plan_duration_days} days trial
                                </div>
                              )}
                              {partner.subscription_end_date && (
                                <div className="text-xs text-gray-500 dark:text-dark-text-secondary mt-1">
                                  {new Date(partner.subscription_end_date) > new Date() ? (
                                    <>Expires: {new Date(partner.subscription_end_date).toLocaleDateString()}</>
                                  ) : (
                                    <span className="text-red-600 dark:text-red-400">Expired</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-dark-text-tertiary">No Plan</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {partner.query_resolver ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                              <XCircle className="h-3 w-3 mr-1" />
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedPartner(partner);
                                setShowEditPartnerModal(true);
                              }}
                              className="text-primary-700 dark:text-dark-primary-400 hover:text-primary-900 dark:hover:text-dark-primary-300 p-1 rounded hover:bg-primary-50 dark:hover:bg-dark-bg-secondary"
                              title="Edit"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'support' && (
        <div className="h-[calc(100vh-200px)]">
          <SupportDashboard />
        </div>
      )}

      {activeTab === 'settings' && (
        <AdminSettings />
      )}

      {/* Modals */}
      {sharingQuestionnaire && (
        <ShareQuestionnaireModal
          isOpen={!!sharingQuestionnaire}
          onClose={(success) => {
            setSharingQuestionnaire(null);
            if (success) {
              // Optionally reload questionnaires
            }
          }}
          questionnaire={sharingQuestionnaire}
          ownerType="admin"
          ownerId={user.id}
        />
      )}

      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateOrganization}
        isLoading={modalLoading}
      />

      <EditOrganizationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOrg(null);
        }}
        onSubmit={handleUpdateOrganization}
        isLoading={modalLoading}
        organization={selectedOrg}
      />

      <OrganizationMetricsModal
        isOpen={showMetricsModal}
        onClose={() => {
          setShowMetricsModal(false);
          setSelectedOrg(null);
          setMetricsData(null);
        }}
        metrics={metricsData}
        isLoading={metricsLoading}
      />

      <EditPartnerModal
        isOpen={showEditPartnerModal}
        onClose={() => {
          setShowEditPartnerModal(false);
          setSelectedPartner(null);
        }}
        onSubmit={handleUpdatePartner}
        isLoading={partnerModalLoading}
        partner={selectedPartner}
      />
    </div>
  );
};

export default AdminDashboard;

